import { MenuController, ModalController } from '@ionic/angular';
import { I18nService, StoreService } from '@core/services';
import { Injectable, QueryList, signal, WritableSignal } from '@angular/core';
import { Observable, debounceTime, distinctUntilChanged, takeUntil, of, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import {
	selectAuth,
	selectClientLoading,
	selectClientLocation,
	selectDeliveryZoneAvailable,
	selectOrderType,
	selectStore,
	selectClient,
	selectLanguaje,
} from '@store/selectors';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
	USER_UPDATE_FORM,
	CLIENT_FIND_LOCATION_SUCCESS,
	CLIENT_UPDATE_DELIVERY_INSTRUCTIONS,
	CLIENT_UPDATE_FORM,
	CLIENT_CREATE_ADDRESS,
	DELIVERY_ZONE_UPDATE_AVAILABLE,
	ORDER_TYPE_UPDATE,
	CART_FIND_TOTALS,
	SUBCATEGORY_FIND_SUCCESS,
	PRODUCT_FIND_SUCCESS,
	SUBCATEGORY_UPDATE_ID,
	DELIVERY_ZONE_RESET,
	STORE_FIND_SUCCESS,
	CHECK_BUSINESS_DAY,
	ORDER_TYPE_FIND_SUCCESS,
	LOCAL_CHANNEL_RESET_STATE,
	CHANNEL_SETTING_CLEAR_STATE,
	DELIVERY_ZONE_FIND_SUCCESS,
	CLIENT_UPDATE_APARTMENT,
	CORE_UPDATE_SLUG,
	STORE_FIND,
} from '@store/actions';
import { Address, Store as StoreModel, DeliveryZone, Client, Schedule, Credential } from '@core/models';
import { ClientService } from '@core/services';
import { IAbstractPresenter, ILanguageObject, IUserLocation, IMessageError } from '@core/interfaces';
import { Router } from '@angular/router';
import { take, withLatestFrom } from 'rxjs/operators';
import { ElementRef } from '@angular/core';
import { GeolocationService } from '@core/services/geolocation/geolocation.service';
import { SwitchService, ServiceType } from '@core/services/switch.service';
import { ComponentEnum, OrderTypeEnum } from '@core/enums';
import { selectApartmentOrSuite, selectDeliveryInstructions } from '@store/selectors/client.selectors';

import { IStoresComponent, IStoresPresenter, IStoreStatus } from './interfaces';
import { toObservable } from '@angular/core/rxjs-interop';
import { StoresProcessor } from './domain';
import { IStoreSelectedEvent } from './components/stores-list/interfaces';
import { StoreDeliveryCodeEnum } from './components/stores-list/enums';

@Injectable({ providedIn: 'root' })
export class StoresPresenter extends IAbstractPresenter<IStoresComponent> implements IStoresPresenter {
	auth$: Observable<Credential | null>;
	formGroup: FormGroup;
	isLoading$: Observable<boolean>;
	client$: Observable<Client | null>;
	isLoggedIn: boolean = false;
	showMap: boolean = true;
	IsModal: boolean = false;
	subcategoryId: string | null = null;
	orderType$: Observable<string | null>;
	address$: Observable<Address[]>;
	storesList$: Observable<StoreModel[] | null>;
	store$: Observable<StoreModel | null>;
	userLocation: { lat: number; lng: number } | null = null;
	clientLocation$: Observable<IUserLocation | null>;
	storesWithDistanceSignal$: WritableSignal<StoreModel[]> = signal([]);
	storesWithDistance$ = toObservable(this.storesWithDistanceSignal$);

	autocompleteResults$: Observable<google.maps.places.AutocompletePrediction[]> = of([]);
	autocompleteService?: google.maps.places.AutocompleteService;
	pickupAddress: string | null = null;
	showNameAddressInput: boolean = false;

	searchAddressInput: string | null = null;
	idAddressDefault: string | null = null;
	selectedOption: ServiceType = OrderTypeEnum.Pickup;
	apartametOrSuite$: Observable<string | null | undefined>;
	deliveryInstructions$: Observable<string | null | undefined>;

	storeInDelivery$: Observable<StoreModel[] | null> = of(null);
	deliveryZoneAvailable$: Observable<DeliveryZone[]> = of([]);
	private deliveryZoneByStoreSlug = new Map<string, DeliveryZone>();
	locationInputs!: QueryList<ElementRef<HTMLInputElement>>;
	locationInput!: ElementRef<HTMLInputElement>;

	translations: ILanguageObject;
	errorMessages: IMessageError;

	constructor(
		private store: Store,
		private formBuilder: FormBuilder,
		private menuCtrl: MenuController,
		private modalCtrl: ModalController,
		private clientService: ClientService,
		private router: Router,
		private geolocationService: GeolocationService,
		private storeService: StoreService,
		private switchService: SwitchService,
		private i18nService: I18nService,
		private storesProcessor: StoresProcessor,
	) {
		super();

		this.auth$ = this.store.select(selectAuth);
		this.client$ = this.store.select(selectClient);
		this.isLoading$ = this.store.select(selectClientLoading);
		this.orderType$ = this.store.select(selectOrderType);
		this.address$ = this.clientService.getAddress();
		this.storesList$ = this.storeService.getStoreWithTimeAvailability();
		this.clientLocation$ = this.store.select(selectClientLocation);
		this.store$ = this.store.select(selectStore);
		this.apartametOrSuite$ = this.store.select(selectApartmentOrSuite);
		this.deliveryInstructions$ = this.store.select(selectDeliveryInstructions);
		this.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore);

		this.errorMessages = {
			apartment: {
				required: (): string => 'Apartment or suite is required',
				minlength: (error: ValidationErrors): string => `Must be at least ${error['requiredLength']} characters`,
				maxlength: (error: ValidationErrors): string => `Cannot exceed ${error['requiredLength']} characters`,
			},
			instructions: {
				minlength: (error: ValidationErrors): string => `Must be at least ${error['requiredLength']} characters`,
				maxlength: (error: ValidationErrors): string => `Cannot exceed ${error['requiredLength']} characters`,
			},
		};
		this.formGroup = this.formBuilder.group({
			address: [null],
			apartment: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(35)]],
			instructions: [null, [Validators.minLength(3), Validators.maxLength(35)]],
			save: [false],
			addressName: [null],
		});
	}

	dispatchActions(): void {
		const isModal = this.view?.IsModal || this.IsModal;
		if (!isModal) {
			this.modalCtrl.getTop().then(top => {
				if (!top && !this.view?.IsModal && !this.IsModal) {
					this.store.dispatch(STORE_FIND_SUCCESS({ store: null }));
					this.store.dispatch(ORDER_TYPE_FIND_SUCCESS({ orderTypeList: null }));
					this.store.dispatch(LOCAL_CHANNEL_RESET_STATE());
					this.store.dispatch(CHANNEL_SETTING_CLEAR_STATE());
				}
			});
		}
	}

	override onAfterViewInit(): void {
		this.findUserLocation();
		this.view.updateSwitchUI?.();

		this.view.locationInputs?.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.view.locationInputs?.forEach(input => {
				this.initAutocomplete(input);
			});
		});

		// Inicializa para los inputs que ya existen
		this.view.locationInputs?.forEach(input => {
			this.initAutocomplete(input);
		});
	}

	async onSwitchChanged(): Promise<void> {
		this.orderType$.pipe(take(1)).subscribe(type => {
			const newType = type === OrderTypeEnum.Pickup ? OrderTypeEnum.Delivery : OrderTypeEnum.Pickup;
			this.store.dispatch(ORDER_TYPE_UPDATE({ orderType: newType }));
			this.switchService.setServiceType(newType);
		});
	}

	confirmDeliveryAddress(): void {
		if (this.formGroup.invalid) {
			this.formGroup.markAllAsTouched();
			return;
		}

		this.store.dispatch(CART_FIND_TOTALS());

		const { save, apartment: apartamento, addressName: nombre } = this.formGroup.value;
		const clientAddressForm = this.storesProcessor.formatClientAddress(
			this.view.searchAddressInput ?? '',
			apartamento,
			nombre
		);

		this.store.dispatch(CLIENT_UPDATE_FORM({ clientAddressForm }));

		if (save) {
			this.store.dispatch(CLIENT_CREATE_ADDRESS());
		}

		const isAddressAvailable = this.addressAvailable();
		const decision = this.storesProcessor.evaluateAddressConfirmation({
			isLoggedIn: this.view.isLoggedIn,
			showNameAddressInput: this.view.showNameAddressInput,
			isAddressAvailable,
		});

		if (decision.shouldDismissModal) {
			this.modalCtrl.dismiss();
		}
		if (decision.shouldResetForm) {
			this.formGroup.reset(
				{ address: null, apartment: null, instructions: null, save: false, addressName: null },
				{ emitEvent: false }
			);
		}
	}

	getRangeTimeByDay(store: StoreModel, code: 'DELIVERY' | 'PICK-UP'): { apertura: string; cierre: string } | null {
		return this.storesProcessor.getRangeTimeByDay(store, code);
	}

	findUserLocation(): void {
		this.view.idAddressDefault = null;

		this.geolocationService
			.getUserLocation()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: location => {
					this.view.searchAddressInput = location?.address ?? '';

					this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location }));
				},
				error: err => {
					console.error('Error obteniendo ubicación del usuario:', err);
				},
			});
	}

	saveManualAddress(inputOrString: HTMLInputElement | string | null | undefined): void {
		const value = typeof inputOrString === 'string' ? inputOrString : inputOrString?.value;

		this.view.idAddressDefault = null;
		this.view.searchAddressInput = value ?? '';

		if (this.storesProcessor.isValidAddressQuery(value)) {
			this.formGroup.get('address')?.setValue(value ?? null);

			if (this.storesProcessor.shouldGeocodeAddress(value, this.pickupAddress)) {
				this.geolocationService
					.getCoordinatesFromAddress(value!)
					.pipe(takeUntil(this.destroy$))
					.subscribe(location => this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location })));
			}
		}
	}

	selectAutocomplete(
		result: google.maps.places.AutocompletePrediction,
		inputElement?: ElementRef<HTMLInputElement | HTMLElement>
	): void {
		const address = result.description;

		if (inputElement?.nativeElement && 'value' in inputElement.nativeElement) {
			(inputElement.nativeElement as HTMLInputElement).value = address;
		}

		this.view.autocompleteResults$ = of([]);
		this.view.searchAddressInput = address;
		this.pickupAddress = address;

		this.geolocationService
			.getCoordinatesFromAddress(address)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: location => {
					this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location }));
				},
				error: err => {
					console.error('Error getting coordinates for address:', err);
				},
			});
	}

	resetInitState(): void {
		this.formGroup.reset(
			{ address: null, apartment: null, instructions: null, save: false, addressName: null },
			{ emitEvent: false }
		);
	}

	updateDeliveryAddress(event: Event): void {
		const target = event.target as HTMLInputElement;
		const value = target.value;
		this.clientService
			.getAddressById(value)
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe(address => {
				this.geolocationService
					.getCoordinatesFromAddress(address?.direccion1)
					.pipe(takeUntil(this.destroy$), take(1))
					.subscribe(coordinates => {
						this.view.searchAddressInput = coordinates?.address ?? null;
						this.formGroup.get('address')?.setValue(coordinates?.address ?? null, { emitEvent: false });

						this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location: coordinates }));
					});
			});
	}

	suscribeObservers(): void {
		this.view.errorMessages = this.errorMessages;

		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.view.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore, lang);
			});

		this.clientService.closeDialog$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.view.showNameAddressInput = false;
		});

		this.clientLocation$.pipe(take(1), takeUntil(this.destroy$)).subscribe(clientLocation => {
			if (!clientLocation?.address) {
				this.findUserLocation();
			} else {
				this.formGroup.get('address')?.setValue(clientLocation.address, { emitEvent: false });
				this.view.searchAddressInput = clientLocation.address ?? '';
				if (this.cdr) {
					this.cdr.detectChanges();
				}
			}
		});

		this.apartametOrSuite$.pipe(takeUntil(this.destroy$)).subscribe(apartametOrSuite => {
			this.formGroup.get('apartment')?.setValue(apartametOrSuite ?? '', { emitEvent: false });
		});

		this.deliveryInstructions$.pipe(takeUntil(this.destroy$)).subscribe(deliveryInstructions => {
			this.formGroup.get('instructions')?.setValue(deliveryInstructions ?? '', { emitEvent: false });
		});

		this.orderType$
			.pipe(
				withLatestFrom(this.store.select(selectApartmentOrSuite), this.store.select(selectDeliveryInstructions)),
				takeUntil(this.destroy$)
			)
			.subscribe(([orderType, apartment, intructions]) => {
				this.selectedOption = (orderType as ServiceType) || OrderTypeEnum.Pickup;
				if (this.view?.updateSwitchUI) {
					this.view.updateSwitchUI();
				}
				if (orderType == OrderTypeEnum.Delivery) {
					this.store
						.select(selectClientLocation)
						.pipe(take(1))
						.subscribe(location => {
							this.view.searchAddressInput = location?.address ?? '';
						});

					if (!this.view.searchAddressInput || this.view.searchAddressInput.length == 0) {
						this.findUserLocation();
					}

					this.formGroup.get('apartment')?.setValue(apartment ?? '', { emitEvent: false });

					this.formGroup.get('instructions')?.setValue(intructions ?? '', { emitEvent: false });
				}
			});

		this.getDeliveryZoneByAddress();

		this.formGroup.valueChanges
			.pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(userForm => {
				this.store.dispatch(USER_UPDATE_FORM({ userForm }));
			});

		combineLatest([this.storesList$, this.clientLocation$])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([stores, location]) => {
				const userLocation = { lat: location?.lat ?? 0, lng: location?.lng ?? 0 };
				this.userLocation = userLocation;

				const overview = this.storesProcessor.processStoresOverview({
					stores,
					userLocation,
					userCountry: location?.countryCode,
					isPointInShape: (point, zone, center) =>
						this.geolocationService.isPointInShape(point, zone, center),
				});

				this.storesWithDistanceSignal$.set(overview.storesWithDistance);
				this.storeInDelivery$ = of(overview.deliveryStores);
				this.view.storeInDelivery$ = of(overview.deliveryStores);
				this.store.dispatch(
					DELIVERY_ZONE_UPDATE_AVAILABLE({ deliveryZoneAvailable: overview.deliveryZonesAvailable })
				);

				if (this.view && this.cdr) {
					this.cdr.detectChanges();
				}
			});

		this.auth$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
			this.view.isLoggedIn = this.storesProcessor.isClientAuthenticated(auth);
		});
	}

	getDeliveryZoneByAddress(): void {
		// La validación ahora se maneja reactivamente en el combineLatest de suscribeObservers
	}

	onOpenCartMenu(): void {
		this.menuCtrl.open('cartMenu');
	}

	onFavoriteChange(event: Event | boolean): void {
		const checked = typeof event === 'boolean' ? event : (event.target as HTMLInputElement)?.checked;
		this.view.showNameAddressInput = checked;
	}

	onAddressClick(address: Address): void {
		const addressString = this.storesProcessor.formatFullAddress(address);
		this.saveManualAddress(addressString);
	}

	closeAddressForm(): void {
		this.view.showNameAddressInput = false;
	}

	getStoreStatus(horarios: Schedule[]): IStoreStatus {
		return this.storesProcessor.getStoreStatus(horarios);
	}

	selectStore(accountSlug: string, slug: string): void {
		this.store.dispatch(CORE_UPDATE_SLUG({ accountSlug, slug }))
		this.store.dispatch(STORE_FIND());

		this.store$.pipe(take(1), takeUntil(this.destroy$)).subscribe(store => {
			if (store?.slug != slug) {
				this.store.dispatch(CHANNEL_SETTING_CLEAR_STATE());
				this.store.dispatch(SUBCATEGORY_FIND_SUCCESS({ subcategory: null }));
				this.store.dispatch(PRODUCT_FIND_SUCCESS({ productList: [] }));
				// this.store.dispatch(CART_FIND_SUCCESS({ items: [] }));
				this.store.dispatch(SUBCATEGORY_UPDATE_ID({ subcategoryId: null }));
				this.store.dispatch(LOCAL_CHANNEL_RESET_STATE());
				this.store.dispatch(ORDER_TYPE_FIND_SUCCESS({ orderTypeList: null }));
			}
		});

		this.router.navigate([`${accountSlug}/`, slug]);
		this.store.dispatch(CART_FIND_TOTALS());
		this.closeModal();
	}

	selectStoreByDelivery(accountSlug: string, slug: string): void {
		if (this.formGroup.invalid) {
			this.formGroup.markAllAsTouched();
			return;
		}
		
		this.store.dispatch(CORE_UPDATE_SLUG({ accountSlug, slug }))
		this.store.dispatch(STORE_FIND());
		const deliveryZone = this.deliveryZoneByStoreSlug.get(slug);
		this.formGroup.get('save')?.setValue(false, { emitEvent: false });
		this.confirmDeliveryAddress();
		if (deliveryZone) {
			this.store.dispatch(DELIVERY_ZONE_FIND_SUCCESS({ deliveryZone }));
		}

		this.store$.pipe(take(1), takeUntil(this.destroy$)).subscribe(store => {
			if (store?.slug != slug) {
				this.store.dispatch(CHANNEL_SETTING_CLEAR_STATE());
				this.store.dispatch(SUBCATEGORY_FIND_SUCCESS({ subcategory: null }));
				this.store.dispatch(PRODUCT_FIND_SUCCESS({ productList: [] }));
				// this.store.dispatch(CART_FIND_SUCCESS({ items: [] }));
				this.store.dispatch(SUBCATEGORY_UPDATE_ID({ subcategoryId: null }));
			}
		});

		this.router.navigate([`${accountSlug}/`, slug]);
		this.store.dispatch(CART_FIND_TOTALS());
		this.cantClosedModal();
	}

	initAutocomplete(inputElement?: ElementRef<HTMLInputElement | HTMLElement>): void {
		if (window.google && google.maps && google.maps.places && inputElement?.nativeElement && 'addEventListener' in inputElement.nativeElement) {
			inputElement.nativeElement.addEventListener('input', (event: Event) => {
				const value = (event.target as HTMLInputElement).value;
				if (this.storesProcessor.isValidAddressQuery(value)) {
					this.searchAutocomplete(value);
				} else {
					this.view.autocompleteResults$ = of([]);
				}
			});
		}
	}

	onApartmentOrSuiteChange(event: Event | string): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLInputElement)?.value;
		this.store.dispatch(CLIENT_UPDATE_APARTMENT({ apartmentOrSuite: value ?? '' }));
	}

	onDeliveryInstructionsChange(event: Event | string): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLTextAreaElement)?.value;
		this.store.dispatch(CLIENT_UPDATE_DELIVERY_INSTRUCTIONS({ deliveryInstructions: value ?? '' }));
	}

	async closeModal(): Promise<void> {
		if (!this.addressAvailable()) {
			this.store.dispatch(CART_FIND_TOTALS());
			this.store.dispatch(DELIVERY_ZONE_RESET());
			this.store.dispatch(ORDER_TYPE_UPDATE({ orderType: OrderTypeEnum.Pickup }));
			this.switchService.setServiceType(OrderTypeEnum.Pickup);
		}

		this.store.dispatch(CHECK_BUSINESS_DAY());
		this.formGroup.get('save')?.setValue(false, { emitEvent: false });
		this.cantClosedModal();
	}

	onAddressInputChange(event: Event | string): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLInputElement)?.value;
		this.saveManualAddress(value ?? '');
		this.searchAutocomplete(value ?? '');
	}

	searchAutocomplete(value: string): void {
		if (this.storesProcessor.isValidAddressQuery(value) && window.google?.maps?.places) {
			if (!this.autocompleteService) {
				this.autocompleteService = new google.maps.places.AutocompleteService();
			}
			this.autocompleteService.getPlacePredictions(
				{
					input: value,
					types: ['address'],
				},
				(predictions, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
						this.view.autocompleteResults$ = of(predictions);
					} else {
						this.view.autocompleteResults$ = of([]);
					}
					if (this.cdr) {
						this.cdr.detectChanges();
					}
				}
			);
		} else {
			this.view.autocompleteResults$ = of([]);
		}
	}

	onMapAddressSelected(address: string): void {
		if (this.view.locationInput?.nativeElement) {
			this.view.locationInput.nativeElement.value = address;
		}
		this.view.searchAddressInput = address;
		this.saveManualAddress(address);
	}

	addressAvailable(): boolean {
		let exist = false;
		this.store
			.select(selectDeliveryZoneAvailable)
			.pipe(take(1), takeUntil(this.destroy$), withLatestFrom(this.store.select(selectStore)))
			.subscribe(([deliveryZonesAvailable, currentStore]) => {
				exist = this.storesProcessor.isAddressAvailable(deliveryZonesAvailable, currentStore);
			});
		return exist;
	}

	async cantClosedModal(): Promise<void> {
		if (await this.modalCtrl.getTop()) await this.modalCtrl.dismiss();
	}

	onStoreSelected({ code,  store: { accountSlug, slug } }: IStoreSelectedEvent): void {
		if (!accountSlug || !slug) return;

		if (code === StoreDeliveryCodeEnum.Delivery) {
			this.selectStoreByDelivery(accountSlug, slug);
		} else {
			this.selectStore(accountSlug, slug);
		}
	}
}
